import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useAuth0 } from '@auth0/auth0-react'; // Assuming you're using Auth0
import SpeechToText from './SpeechToText';
import { ChatMarkdown } from './ChatMarkdown';
import { useCards, useActions } from '../../utils/reactQueryHooks';
import { useCardData } from '../common/CardContext';
import { useChatUIPersistence } from './ChatUIPersistenceContext';

/** Max user+assistant messages sent as `conversationHistory` (bootstrap until server persists turns). */
const MAX_AGENT_CONVERSATION_HISTORY_MESSAGES = 16;

function buildConversationHistoryForAgent(messages) {
    const list = Array.isArray(messages) ? messages : [];
    const turns = list.filter((m) => m.type === 'user' || m.type === 'ai');
    const slice = turns.slice(-MAX_AGENT_CONVERSATION_HISTORY_MESSAGES);
    return slice.map((m) => ({
        role: m.type === 'user' ? 'user' : 'assistant',
        content: String(m.text ?? ''),
    }));
}

function readHistoryPersistedFlag(result) {
    if (!result || typeof result !== 'object') return false;
    return !!(
        result.historyPersisted
        || result.conversationHistoryPersisted
        || result.history_persisted
        || result.conversation_history_persisted
    );
}

/** Per-sensor live snapshot for agent tools (irrigation readiness, tray→pump routing). camelCase; backend may map e.g. distanceCm → distance_cm. */
function buildLiveReadingSnapshot(cardData, macAddress) {
    if (!macAddress || !cardData?.[macAddress]) return null;
    const d = cardData[macAddress];
    return {
        temperature: d.mostRecentTemp ?? null,
        humidity: d.mostRecentHumidity ?? null,
        moisture: d.mostRecentMoisture ?? null,
        distanceCm: d.mostRecentDistance ?? null,
        distance2Cm: d.mostRecentDistance2 ?? null,
        water: d.mostRecentWater ?? null,
        water2: d.mostRecentWater2 ?? null,
        lightLux: d.mostRecentLight ?? null,
        timestamp: d.mostRecentTimestamp ?? null,
    };
}

export default function ChatBot({ 
    isModal = true, 
    closeChatModal, 
    handleClearChatHistory, 
    modalPosition = 'center',
    metaData = {} // Add metaData prop to get sensorMacAddress
}) {
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [currentAIMessage, setCurrentAIMessage] = useState(''); // State for the current AI message
    const [textToSpeechActive, setTextToSpeechActive] = useState(false); // State for text-to-speech toggle
    
    // Speech recognition related state
    const speechToTextRef = useRef(null);
    const [isSpeechActive, setIsSpeechActive] = useState(false);
    
    const { user } = useAuth0();
    const {
        messages,
        setMessages,
        agentSessionId,
        setAgentSessionId,
        serverHasConversationRows,
        setServerHasConversationRows,
        clearPersistedChat,
    } = useChatUIPersistence();
    const { data: cards } = useCards();
    const { data: actions } = useActions();
    const { cardData } = useCardData();

    // REACT_APP_AGENT_SERVER = agent base path including /api/agent, e.g. http://host:5001/api/agent (no trailing slash)
    // POST {base}/{username}, DELETE {base}/{username}/session/{session_id}
    // eslint-disable-next-line no-undef
    const agentServer = process.env.REACT_APP_AGENT_SERVER;

    /**
     * Build enhanced context for the Agent Planner (sensors, actions, real-time data).
     * See READMES/Chatbot/SEMANTIC_LAYER_README.md for contract.
     */
    const prepareEnhancedContext = useCallback(() => {
        const list = cards ?? [];
        const sensorRealTimeByMac = Object.fromEntries(
            list.map((card) => {
                const mac = card.macAddress;
                return [mac, buildLiveReadingSnapshot(cardData, mac)];
            }),
        );
        return {
            macAddress: metaData.macAddress ?? null,
            actionType: metaData.actionType ?? null,
            timeRange: metaData.timeRange ?? null,
            /** Focused card only (legacy); prefer `liveReading` on each sensor + `sensorRealTimeByMac`. */
            realTimeData: metaData.macAddress && cardData?.[metaData.macAddress]
                ? cardData[metaData.macAddress]
                : null,
            /** Every known sensor MAC → live snapshot (distanceCm etc.) for irrigation / readiness tools. */
            sensorRealTimeByMac,
            sensors: list.map((card) => ({
                macAddress: card.macAddress,
                description: card.sensorDescription,
                sensorDescription: card.sensorDescription,
                liveReading: buildLiveReadingSnapshot(cardData, card.macAddress),
                capabilities: {
                    // Temp/humidity are always flagged so history/realtime queries align with Influx;
                    // automation still only runs when a paired action exists (water/light thresholds below).
                    temperature: true,
                    humidity: true,
                    water: !!(card.waterLow ?? card.waterHigh),
                    light: !!(card.lightLow ?? card.lightHigh),
                },
                thresholds: {
                    temperature: {
                        low: card.temperatureLow ?? null,
                        high: card.temperatureHigh ?? null,
                    },
                    humidity: {
                        low: card.humidityLow ?? null,
                        high: card.humidityHigh ?? null,
                    },
                    water: {
                        low: card.waterLow ?? null,
                        high: card.waterHigh ?? null,
                    },
                    water2: {
                        low: card.water2Low ?? null,
                        high: card.water2High ?? null,
                    },
                    light: {
                        low: card.lightLow ?? null,
                        high: card.lightHigh ?? null,
                    },
                },
            })),
            actions: (actions ?? []).map((action) => ({
                macAddress: action.macAddress,
                description: action.actuatorDescription,
                actuatorDescription: action.actuatorDescription,
                type: action.actuatorType,
                sensor: action.sensor,
                status: action.status,
                ...(action.tracking != null && { tracking: action.tracking }),
                ...(action.duration != null && { duration: action.duration }),
                ...(action.sensorTracking !== undefined && { sensorTracking: action.sensorTracking }),
            })),
        };
    }, [metaData.macAddress, metaData.actionType, metaData.timeRange, cardData, cards, actions]);

    const sendAgentRequest = async (message, priorMessagesForHistory = []) => {
        if (!agentServer) {
            try {
                await new Promise((resolve) => setTimeout(resolve, 800));
                const lowerMessage = message.toLowerCase();
                let mockResponse = '';
                if (lowerMessage.includes('hello') || lowerMessage.includes('hi')) {
                    mockResponse = "Hello! 👋 I'm your Canopy AI Assistant. I'm here to help you monitor and manage your plants. How can I assist you today?";
                } else if (lowerMessage.includes('temperature') || lowerMessage.includes('temp')) {
                    mockResponse = "Based on your sensor data, the current temperature is 72°F. This is within the optimal range for most plants. Would you like me to set up alerts for temperature changes?";
                } else if (lowerMessage.includes('water') || lowerMessage.includes('irrigation')) {
                    mockResponse = "Your irrigation system is currently set to trigger every 2 days with a 4-second duration. The soil moisture levels look healthy. Would you like to adjust these settings?";
                } else if (lowerMessage.includes('help')) {
                    mockResponse = "I can help you with:\n\n• Monitoring sensor data (temperature, humidity, moisture)\n• Managing irrigation schedules\n• Setting up automation rules\n• Analyzing plant health trends\n• Troubleshooting device issues\n\nWhat would you like to know more about?";
                } else {
                    mockResponse = `I received your message: "${message}". Set REACT_APP_AGENT_SERVER to use the real agent. 🌱`;
                }
                return { success: true, data: { content: mockResponse } };
            } catch (error) {
                console.error('Error in mock response:', error);
                return { success: false, error: `Failed to process request: ${error.message}` };
            }
        }

        try {
            const username = user?.nickname || user?.email || 'anonymous';
            const context = prepareEnhancedContext();
            const conversationHistory =
                !serverHasConversationRows
                    ? buildConversationHistoryForAgent(priorMessagesForHistory)
                    : [];
            const requestPayload = {
                message,
                context,
                /** One stable id per chat thread (persisted in ChatUIPersistence); required for pending MQTT proposals keyed by username + sessionId. */
                ...(agentSessionId && { sessionId: agentSessionId }),
                /** Ask server to return focus hints for follow-ups (“same tray”). */
                includeFocus: true,
                ...(conversationHistory.length > 0 ? { conversationHistory } : {}),
            };

            const base = agentServer.replace(/\/$/, '');
            const url = `${base}/${encodeURIComponent(username)}`;
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(requestPayload),
            });

            if (!response.ok) {
                throw new Error(`Agent request failed: ${response.status}`);
            }

            const result = await response.json();

            if (result.sessionId) {
                setAgentSessionId(result.sessionId);
            }
            if (readHistoryPersistedFlag(result)) {
                setServerHasConversationRows(true);
            }

            // Optional (when backend adds it): if result.actionRequest, call publishMQTTStatus(...) and show outcome in chat
            // if (result.actionRequest) { ... }

            const content = result.response ?? result.message ?? 'Agent processed your request.';
            return { success: true, data: { content } };
        } catch (error) {
            console.error('Error sending request to agent:', error);
            return {
                success: false,
                error: `Failed to process request: ${error.message}`,
            };
        }
    };
    
    // Dragging state with local storage persistence
    const [isDragging, setIsDragging] = useState(false);
    const [hasJustDragged, setHasJustDragged] = useState(false);
    const dragSensitivity = 0.7; // Drag speed reduction factor
    const [lastMousePosition, setLastMousePosition] = useState({ x: 0, y: 0 });
    const [dragStartPosition, setDragStartPosition] = useState({ x: 0, y: 0 });
    const minDragDistance = 5; // Minimum distance to start dragging
    const [position, setPosition] = useState(() => {
        // Initialize position from local storage or default to 0,0
        const savedPosition = localStorage.getItem('chatModalPosition');
        return savedPosition 
            ? JSON.parse(savedPosition) 
            : { x: 0, y: 0 };
    });

    const modalRef = useRef(null);
    // Create a ref to hold the typing interval ID
    const typingIntervalRef = useRef(null);

    //Ref for chat messages container:
    const chatMessagesRef = useRef(null);
    //Ref to track if scroll should be at bottom:
    const shouldScrollRef = useRef(true);

    useEffect(() => {
        if (chatMessagesRef.current && shouldScrollRef.current) {
            chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
        }
    }, [messages]);

    // Persist position when it changes
    useEffect(() => {
        localStorage.setItem('chatModalPosition', JSON.stringify(position));
    }, [position]);

    // ChatBot initialization
    useEffect(() => {
        // Any initialization code can go here if needed in the future
    }, []);

    // Log session ID changes for debugging
    useEffect(() => {
        if (agentSessionId) {
            console.log('Session ID updated:', agentSessionId);
        }
    }, [agentSessionId]);

    // Drag start handler
    const handleDragStart = (e) => {
        // Allow dragging from the entire modal, not just the header
        // But prevent dragging from interactive elements like buttons and inputs
        const isInteractiveElement = e.target.closest('button') || 
                                   e.target.closest('input') || 
                                   e.target.closest('textarea') ||
                                   e.target.closest('.chat-messages') ||
                                   e.target.closest('.chat-modal-input') ||
                                   e.target.closest('.chat-message') ||
                                   e.target.closest('.thinking-dots');
        
        // Also prevent dragging if user is selecting text
        const isTextSelection = window.getSelection().toString().length > 0;
        
        if (!isInteractiveElement && !isTextSelection) {
            setDragStartPosition({ x: e.clientX, y: e.clientY });
            setLastMousePosition({ x: e.clientX, y: e.clientY });
            setIsDragging(true);
            setHasJustDragged(false);
        }
    };

    // Drag move handler
    const handleDragMove = useCallback((e) => {
        if (isDragging) {
            // Calculate total distance moved from start
            const totalDeltaX = e.clientX - dragStartPosition.x;
            const totalDeltaY = e.clientY - dragStartPosition.y;
            const totalDistance = Math.sqrt(totalDeltaX * totalDeltaX + totalDeltaY * totalDeltaY);
            
            // Only start moving if we've moved beyond the minimum distance
            if (totalDistance > minDragDistance) {
                // Calculate the difference with reduced sensitivity
                const deltaX = (e.clientX - lastMousePosition.x) * dragSensitivity;
                const deltaY = (e.clientY - lastMousePosition.y) * dragSensitivity;

                setPosition(prev => ({
                    x: prev.x + deltaX,
                    y: prev.y + deltaY
                }));

                // Update last mouse position
                setLastMousePosition({ x: e.clientX, y: e.clientY });
                setHasJustDragged(true);
            }
        }
    }, [isDragging, lastMousePosition, dragSensitivity, dragStartPosition, minDragDistance]);

    // Drag end handler
    const handleDragEnd = useCallback(() => {
        setIsDragging(false);
    }, []);

    // Add event listeners for dragging
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleDragMove);
            document.addEventListener('mouseup', handleDragEnd);
        }

        return () => {
            document.removeEventListener('mousemove', handleDragMove);
            document.removeEventListener('mouseup', handleDragEnd);
        };
    }, [isDragging, handleDragMove, handleDragEnd]);

    useEffect(() => {
        return () => clearInterval(typingIntervalRef.current); // Cleanup on unmount
    }, []);

    // Reset position method
    const resetModalPosition = () => {
        setPosition({ x: 0, y: 0 });
        localStorage.removeItem('chatModalPosition');
    };

    const clearChatAndSession = () => {
        const sid = agentSessionId;
        if (agentServer && sid) {
            const base = agentServer.replace(/\/$/, '');
            const u = user?.nickname || user?.email || 'anonymous';
            fetch(`${base}/${encodeURIComponent(u)}/session/${encodeURIComponent(sid)}`, {
                method: 'DELETE',
            }).catch(() => {});
        }
        clearPersistedChat();
        if (handleClearChatHistory) {
            handleClearChatHistory();
        }
    };

    // Handle overlay click with drag prevention
    const handleOverlayClick = (e) => {
        // Only close if not just dragged and clicked outside the modal
        if (!hasJustDragged && e.target === e.currentTarget) {
            closeChatModal();
        }
    };

    // Function to simulate typing animation with smooth, ChatGPT-like effect
    const startTypingAnimation = (fullMessage) => {
        setCurrentAIMessage(''); // Reset current message
        
        // For very long messages, use a faster typing speed
        const shouldUseFastTyping = fullMessage.length > 500;
        
        let index = 0;
        // ChatGPT-like typing speed: smooth and natural
        const baseSpeed = shouldUseFastTyping ? 3 : 15; // milliseconds per character
        
        typingIntervalRef.current = setInterval(() => {
            if (index < fullMessage.length) {
                // Determine chunk size for natural typing
                let chunkSize = 1;
                
                // Type faster through whitespace and punctuation
                if (fullMessage[index] === ' ' || fullMessage[index] === '\n') {
                    chunkSize = shouldUseFastTyping ? 3 : 1;
                } else if ([',', '.', '!', '?', ';', ':'].includes(fullMessage[index])) {
                    chunkSize = 1;
                    // Add a slight pause after sentences (handled by not incrementing extra)
                } else if (shouldUseFastTyping) {
                    chunkSize = Math.floor(Math.random() * 3) + 1; // 1-3 chars for long messages
                }
                
                const nextIndex = Math.min(index + chunkSize, fullMessage.length);
                const chunk = fullMessage.substring(index, nextIndex);
                
                setCurrentAIMessage(prev => prev + chunk);
                index = nextIndex;
            } else {
                clearInterval(typingIntervalRef.current); // Clear interval when done
                const aiMessageObj = {
                    id: Date.now() + Math.random(),
                    type: 'ai',
                    text: fullMessage, // Use the full message here
                    timestamp: new Date().toISOString()
                };
                
                setMessages(prevMessages => [...prevMessages, aiMessageObj]);
                setCurrentAIMessage(''); // Reset to empty after adding to messages
            }
        }, baseSpeed);
    };

    // Handle message sending
    // Method to handle sending the aggregated speech text (for future enhancements)
    // This function is intentionally defined for future use and documented here
    // It will be used when implementing more advanced speech aggregation features
    // such as command processing or voice-driven analytics for sensor data
    // eslint-disable-next-line no-unused-vars
    const handleSendAggregatedSpeech = useCallback(() => {
        if (speechToTextRef.current) {
            const aggregatedText = speechToTextRef.current.getAggregatedText();
            if (aggregatedText.trim()) {
                setInputMessage(aggregatedText.trim());
                // Reset the aggregated text after using it
                speechToTextRef.current.resetAggregatedText();
                // Optional: auto-send the message
                // setTimeout(() => handleSendMessage(), 100);
            }
        }
    }, []);
    
    
    const handleSendMessage = async () => {
        if (!inputMessage.trim()) return;
        
        // If speech is active, stop it before sending the message
        if (isSpeechActive && speechToTextRef.current) {
            speechToTextRef.current.stopListening();
        }

        shouldScrollRef.current = true;
    
        const userMessageObj = {
            id: Date.now(),
            type: 'user',
            text: inputMessage,
            timestamp: new Date().toISOString()
        };
    
        const updatedMessages = Array.isArray(messages) 
            ? [...messages, userMessageObj] 
            : [userMessageObj];
        
        setMessages(updatedMessages);
        setInputMessage('');
        setIsLoading(true);
    
        try {
            const response = await sendAgentRequest(inputMessage, messages);
    
            if (response.success) {
                const fullMessage = response.data.content;
                startTypingAnimation(fullMessage); // Pass the message to the typing function

            } else {
                const errorMessageObj = {
                    id: Date.now() + 1,
                    type: 'error',
                    text: response.error || 'An error occurred while processing your message',
                    timestamp: new Date().toISOString()
                };
                
                const finalUpdatedMessages = Array.isArray(messages)
                    ? [...messages, userMessageObj, errorMessageObj]
                    : [userMessageObj, errorMessageObj];
                
                setMessages(finalUpdatedMessages);
            }
        } catch (error) {
            console.error('Message sending error:', error);
            
            const errorMessageObj = {
                id: Date.now() + 1,
                type: 'error',
                text: 'Failed to send message. Please try again.',
                timestamp: new Date().toISOString()
            };
            
            const finalUpdatedMessages = Array.isArray(messages)
                ? [...messages, errorMessageObj]
                : [errorMessageObj];
            
            setMessages(finalUpdatedMessages);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSendMessage();
        }
    };

    const renderChatContent = () => (
        <div className="chatbot-container">
            <div className="chat-modal-body">
                <div className="chat-messages" ref={chatMessagesRef}>
                    {messages.map((msg) => (
                        <div key={msg.id} className={`chat-message-wrapper ${msg.type}`}>
                            <div className="message-avatar">
                                {msg.type === 'ai' ? (
                                    <div className="avatar-icon ai-avatar">
                                        <i className="ri-plant-line"></i>
                                    </div>
                                ) : (
                                    <div className="avatar-icon user-avatar">
                                        <i className="ri-user-line"></i>
                                    </div>
                                )}
                            </div>
                            <div className={`chat-message ${msg.type}`}>
                                {msg.type === 'ai' ? (
                                    <ChatMarkdown content={msg.text} />
                                ) : (
                                    <div className="message-content message-content--plain">{msg.text}</div>
                                )}
                            </div>
                        </div>
                    ))}
                    {isLoading && (
                        <div className="chat-message-wrapper ai">
                            <div className="message-avatar">
                                <div className="avatar-icon ai-avatar">
                                    <i className="ri-plant-line"></i>
                                </div>
                            </div>
                            <div className="chat-message ai thinking">
                                <div className="thinking-dots">
                                    <span className="dot"></span>
                                    <span className="dot"></span>
                                    <span className="dot"></span>
                                </div>
                            </div>
                        </div>
                    )}
                    {currentAIMessage && (
                        <div className="chat-message-wrapper ai typing">
                            <div className="message-avatar">
                                <div className="avatar-icon ai-avatar">
                                    <i className="ri-plant-line"></i>
                                </div>
                            </div>
                            <div className="chat-message ai chat-message--streaming">
                                <ChatMarkdown content={currentAIMessage} />
                                <span className="typing-cursor">▊</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <div className="chat-modal-input">
                <div className="input-controls">
                    <SpeechToText
                        ref={speechToTextRef}
                        onTextReceived={(text) => {
                            setInputMessage(prev => prev + text);
                        }}
                        onSpeechStateChange={(isActive) => setIsSpeechActive(isActive)}
                        disabled={isLoading}
                        buttonClassName="speech-toggle"
                        activeClass="active"
                        buttonTitle="Toggle Speech-to-Text"
                        showIconOnly={true}
                    />
                    <textarea
                        type="text"
                        placeholder={isSpeechActive ? "Listening..." : "Message Canopy AI..."}
                        value={inputMessage}
                        onChange={(e) => setInputMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        disabled={isLoading}
                        className={isSpeechActive ? 'speech-active-textarea' : ''}
                    />
                    <div className="send-controls">
                        <button
                            className={`speech-output-toggle ${textToSpeechActive ? 'active' : ''}`}
                            onClick={() => setTextToSpeechActive(!textToSpeechActive)}
                            title="Toggle Text-to-Speech"
                        >
                            <i className={`ri-volume-${textToSpeechActive ? 'up-fill' : 'mute-line'}`}></i>
                        </button>
                        <button 
                            className="send-button"
                            onClick={handleSendMessage}
                            disabled={isLoading || inputMessage.trim() === ''}
                            title="Send Message"
                        >
                            <i className="ri-send-plane-fill"></i>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    // Render as modal or inline component based on isModal prop
    if (isModal) {
        return (
            <div>
                <div 
                    className={`chat-modal-overlay ${
                        modalPosition === 'left' ? 'position-left' :
                        modalPosition === 'right' ? 'position-right' :
                        modalPosition === 'top' ? 'position-top' :
                        modalPosition === 'bottom' ? 'position-bottom' :
                        modalPosition === 'center-left' ? 'position-center-left' :
                        modalPosition === 'center-right' ? 'position-center-right' :
                        '' // Default to center
                    }`} 
                    onClick={handleOverlayClick}
                >
                    <div 
                        ref={modalRef} 
                        className="chat-modal" 
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={handleDragStart}
                        style={{ 
                            transform: `translate(${position.x}px, ${position.y}px)`,
                            cursor: isDragging ? 'grabbing' : 'grab'
                        }}
                    >
                        <div 
                            className="chat-modal-header"
                            style={{ cursor: 'move' }}
                        >
                            <h3>Canopy AI Assistant</h3>
                            <div className="chat-header-actions">
                                <button 
                                    className="clear-chat-btn"
                                    onClick={clearChatAndSession}
                                    title="Clear Chat History"
                                >
                                    <i className="ri-delete-bin-line"></i>
                                </button>
                                <button onClick={closeChatModal}>×</button>
                                <button 
                                    onClick={resetModalPosition}
                                    title="Reset Modal Position"
                                >
                                    <i className="ri-refresh-line"></i>
                                </button>
                            </div>
                        </div>
                        {renderChatContent()}
                    </div>
                </div>
            </div>
        );
    }

    // Inline component rendering
    return (
        <div className="chat-container">
            {renderChatContent()}
        </div>
    );
}

ChatBot.propTypes = {
    isModal: PropTypes.bool,
    closeChatModal: PropTypes.func,
    handleClearChatHistory: PropTypes.func,
    modalPosition: PropTypes.oneOf([
        'center', 
        'left', 
        'right', 
        'top', 
        'bottom', 
        'center-left', 
        'center-right'
    ]),
    metaData: PropTypes.shape({
        macAddress: PropTypes.string,
        timeRange: PropTypes.string
    })
};